import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../context/JourneyContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, School, GraduationCap, Users, Briefcase, Handshake, Check, Mail, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USER_TYPES = [
  {
    id: 'Primary or Intermediate School',
    title: "I'm a Primary or Intermediate School",
    description: 'Building confidence across the whole school',
    icon: School,
  },
  {
    id: 'High School',
    title: "I'm a High School",
    description: 'Structured, systemised movement delivery',
    icon: GraduationCap,
  },
  {
    id: 'Teacher / Leadership PL',
    title: "I'm looking for Teacher / Leadership PL",
    description: 'Leadership & wellbeing programs',
    icon: Users,
  },
  {
    id: 'Company or Workplace',
    title: 'I represent a Company or Workplace',
    description: 'Workplace wellbeing sessions',
    icon: Briefcase,
  },
  {
    id: 'Partnership or Sponsorship',
    title: "I'm interested in Partnership or Sponsorship",
    description: 'Strategic education partnerships',
    icon: Handshake,
  },
];

const UserTypeSelection = () => {
  const navigate = useNavigate();
  const { journey, updateJourney, trackPageView } = useJourney();
  const [selected, setSelected] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showSoftExit, setShowSoftExit] = useState(false);
  const [softExitEmail, setSoftExitEmail] = useState('');
  const [softExitLoading, setSoftExitLoading] = useState(false);

  React.useEffect(() => {
    trackPageView('user_type_selection');
  }, [trackPageView]);

  const handleSelect = async (userType) => {
    setSelected(userType.id);
    setIsTransitioning(true);
    
    await updateJourney({ user_type: userType.id });
    
    // Auto-advance after brief visual feedback
    setTimeout(() => {
      navigate('/recommendation');
    }, 300);
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleSoftExit = async () => {
    if (!softExitEmail) return;
    
    setSoftExitLoading(true);
    try {
      await axios.post(`${API}/soft-exit`, {
        email: softExitEmail,
        journey_id: journey?.id,
        user_type: selected,
        recommended_pathway: null
      });
      toast.success('Summary sent! Check your inbox.');
      setShowSoftExit(false);
      setSoftExitEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSoftExitLoading(false);
    }
  };

  return (
    <div 
      data-testid="user-type-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background - clean, no texture */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Back button - always visible */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={handleBack}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-10 animate-fade-in-up">
          <h1 
            data-testid="choose-path-heading"
            className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4"
          >
            What best describes your situation?
          </h1>
        </div>

        {/* Cards List */}
        <div className="space-y-4 max-w-2xl mx-auto">
          {USER_TYPES.map((userType, index) => {
            const Icon = userType.icon;
            const isSelected = selected === userType.id;
            
            return (
              <Card
                key={userType.id}
                data-testid={`user-type-card-${userType.id.toLowerCase().replace(/[^a-z]/g, '-')}`}
                onClick={() => handleSelect(userType)}
                className={cn(
                  "relative cursor-pointer p-5 rounded-2xl border-2 transition-all duration-300",
                  "bg-stone-900/50",
                  "hover:scale-[1.01] hover:border-primary/50 active:scale-[0.99]",
                  "animate-fade-in-up opacity-0",
                  "flex items-center gap-4",
                  isSelected ? "border-primary ring-2 ring-primary/20" : "border-stone-700",
                  isTransitioning && !isSelected && "opacity-50"
                )}
                style={{ 
                  animationDelay: `${0.1 + index * 0.08}s`, 
                  animationFillMode: 'forwards' 
                }}
              >
                {/* Icon */}
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
                  "bg-stone-800"
                )}>
                  <Icon className={cn(
                    "w-6 h-6",
                    isSelected ? "text-primary" : "text-stone-300"
                  )} />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-base font-heading font-bold text-white">
                    {userType.title}
                  </h3>
                  <p className="text-sm text-stone-400">
                    {userType.description}
                  </p>
                </div>

                {/* Selection indicator */}
                <div className={cn(
                  "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0",
                  isSelected 
                    ? "border-primary bg-primary" 
                    : "border-stone-600"
                )}>
                  {isSelected && <Check className="w-4 h-4 text-white" />}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Soft Exit Option */}
        <div className="mt-10 text-center">
          {!showSoftExit ? (
            <button
              data-testid="soft-exit-trigger"
              onClick={() => setShowSoftExit(true)}
              className="text-sm text-stone-500 hover:text-stone-300 transition-colors underline underline-offset-4"
            >
              Not ready yet? Email me a summary.
            </button>
          ) : (
            <div className="bg-stone-900/80 border border-stone-800 rounded-2xl p-6 animate-fade-in-up max-w-md mx-auto">
              <p className="text-stone-300 mb-4 text-sm">
                We'll send you a summary of your options so you can explore later.
              </p>
              <div className="flex gap-2">
                <Input
                  data-testid="soft-exit-email"
                  type="email"
                  placeholder="your@email.com"
                  value={softExitEmail}
                  onChange={(e) => setSoftExitEmail(e.target.value)}
                  className="rounded-xl h-12 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500"
                />
                <Button
                  data-testid="soft-exit-submit"
                  onClick={handleSoftExit}
                  disabled={!softExitEmail || softExitLoading}
                  className="rounded-xl px-4 bg-primary hover:bg-primary/90"
                >
                  {softExitLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                </Button>
              </div>
              <button
                onClick={() => setShowSoftExit(false)}
                className="text-xs text-stone-500 hover:text-stone-400 mt-3"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserTypeSelection;
